<script>
  /**
   * Pricing component — renders subscription packages from the API.
   * @prop {Array} packages - Subscription packages from GET /api/subscription
   */
  export let packages = [];

  let yearly = false;

  function toggleBilling() {
    yearly = !yearly;
  }

  // Sort packages by their `order` field from the API
  $: sortedPackages = [...packages]
    .filter((pkg) => pkg.name?.toLowerCase() !== "test")
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // The "highlighted" package is the middle one (order 2), or the second if only two
  $: highlightIndex = sortedPackages.length === 3 ? 1 : -1;

  // Format price for display
  function formatPrice(price) {
    const num = parseFloat(price);
    if (!num || num === 0) return "Free";
    return num.toLocaleString("en-KE");
  }

  function isFree(pkg) {
    return parseFloat(pkg.monthlyPrice) === 0;
  }
</script>

<div id="pricing" class="mt-28">
  <div class="text-center">
    <h1 class="text-3xl lg:text-5xl font-bold">
      Competitive pricing for any <span class="text-green-500">Business.</span>
    </h1>
    <p class="text-gray-500 mt-3 text-lg max-w-xl mx-auto">
      Choose a plan that scales with your business. Upgrade anytime.
    </p>
  </div>

  <!-- Billing toggle -->
  <div class="my-10 flex items-center justify-center gap-3">
    <span
      class="text-base font-semibold {!yearly
        ? 'text-gray-900'
        : 'text-gray-400'} transition-colors"
    >
      Monthly
    </span>
    <button
      type="button"
      on:click={toggleBilling}
      class="relative inline-flex h-8 w-[56px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none {yearly
        ? 'bg-[#191919]'
        : 'bg-green-500'}"
      role="switch"
      aria-checked={yearly}
    >
      <span
        class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition-transform duration-300 ease-in-out {yearly
          ? 'translate-x-[26px]'
          : 'translate-x-0'}"
      />
    </button>
    <span
      class="text-base font-semibold {yearly
        ? 'text-gray-900'
        : 'text-gray-400'} transition-colors"
    >
      Yearly
      <span
        class="ml-1 inline-block bg-green-100 text-green-700 text-xs font-bold px-2.5 py-0.5 rounded-full"
      >
        Save 20%
      </span>
    </span>
  </div>

  <!-- Package cards -->
  {#if sortedPackages.length > 0}
    <div
      class="container mx-auto grid grid-cols-1 gap-6 mt-6 px-4 {sortedPackages.length >=
      3
        ? 'md:grid-cols-3'
        : sortedPackages.length === 2
          ? 'md:grid-cols-2'
          : 'md:grid-cols-1'}"
    >
      {#each sortedPackages as pkg, i}
        {@const isHighlighted = i === highlightIndex}
        {@const isLast =
          i === sortedPackages.length - 1 && sortedPackages.length >= 3}
        {@const free = isFree(pkg)}
        {@const mainFeatures = pkg.features?.[0]?.main_features || []}
        {@const subFeatures = pkg.features?.[0]?.sub_features || []}

        <div
          class="relative rounded-2xl p-7 flex flex-col transition-all duration-300 hover:shadow-xl
            {isHighlighted
            ? 'bg-green-500 text-white shadow-xl shadow-green-500/20 scale-[1.03] z-10'
            : isLast
              ? 'bg-[#E6F9F1] text-gray-900 shadow-md'
              : 'bg-white text-gray-900 shadow-md border border-gray-100'}"
        >
          <!-- Popular badge -->
          {#if isHighlighted}
            <div class="absolute -top-4 left-1/2 -translate-x-1/2">
              <span
                class="bg-white text-green-600 text-xs font-bold px-4 py-1.5 rounded-full shadow-md"
              >
                Most Popular
              </span>
            </div>
          {/if}

          <!-- Package header -->
          <div class="mb-5">
            <h2 class="text-2xl font-bold">{pkg.name}</h2>
            <p
              class="text-sm mt-1 {isHighlighted
                ? 'text-green-100'
                : 'text-gray-500'}"
            >
              {pkg.description || ""}
            </p>
          </div>

          <!-- Price -->
          <div
            class="pb-5 mb-5 border-b {isHighlighted
              ? 'border-white/30'
              : 'border-gray-200'}"
          >
            {#if free}
              <div class="flex items-baseline gap-1">
                <span class="text-4xl font-extrabold">Free</span>
              </div>
              <p
                class="text-sm mt-1 {isHighlighted
                  ? 'text-green-100'
                  : 'text-gray-500'}"
              >
                No credit card required
              </p>
            {:else}
              <div class="flex items-baseline gap-1">
                <span
                  class="text-sm font-medium {isHighlighted
                    ? 'text-green-100'
                    : 'text-gray-500'}"
                >
                  Ksh
                </span>
                <span class="text-4xl font-extrabold">
                  {yearly
                    ? formatPrice(pkg.yearlyPrice)
                    : formatPrice(pkg.monthlyPrice)}
                </span>
                <span
                  class="text-sm {isHighlighted
                    ? 'text-green-100'
                    : 'text-gray-500'}"
                >
                  /{yearly ? "year" : "month"}
                </span>
              </div>
              {#if yearly && pkg.monthlyPrice}
                <p
                  class="text-sm mt-1 {isHighlighted
                    ? 'text-green-100'
                    : 'text-gray-500'}"
                >
                  Ksh {formatPrice(pkg.monthlyPrice)}/mo billed annually
                </p>
              {/if}
            {/if}
          </div>

          <!-- CTA Button -->
          <div class="mb-6">
            <a
              class="block text-center font-semibold text-base py-3.5 rounded-full transition-all duration-200
                {isHighlighted
                ? 'bg-white text-green-600 hover:bg-gray-50 shadow-md'
                : 'bg-green-500 text-white hover:bg-green-600 shadow-md hover:shadow-lg'}"
              href="/sign-up"
            >
              Get Started
            </a>
          </div>

          <!-- Sub features (What's Included) -->
          {#if subFeatures.length > 0}
            <div class="flex-1">
              <h4
                class="text-sm font-semibold uppercase tracking-wider mb-3 {isHighlighted
                  ? 'text-green-100'
                  : 'text-gray-400'}"
              >
                What's Included
              </h4>
              <ul class="space-y-2.5">
                {#each subFeatures as feature}
                  <li class="flex items-start gap-2.5 text-sm">
                    <svg
                      class="w-4 h-4 mt-0.5 flex-shrink-0 {isHighlighted
                        ? 'text-green-200'
                        : 'text-green-500'}"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{feature}</span>
                  </li>
                {/each}
              </ul>
            </div>
          {/if}

          <!-- Main features -->
          {#if mainFeatures.length > 0}
            <div
              class="mt-6 pt-5 border-t {isHighlighted
                ? 'border-white/30'
                : 'border-gray-200'}"
            >
              <h4
                class="text-sm font-semibold uppercase tracking-wider mb-3 {isHighlighted
                  ? 'text-green-100'
                  : 'text-gray-400'}"
              >
                Key Highlights
              </h4>
              <ul class="space-y-2.5">
                {#each mainFeatures as feature}
                  <li class="flex items-center gap-2.5 text-sm font-medium">
                    <span
                      class="flex items-center justify-center w-5 h-5 rounded-full {isHighlighted
                        ? 'bg-white/20'
                        : 'bg-green-100'}"
                    >
                      <svg
                        class="w-3 h-3 {isHighlighted
                          ? 'text-white'
                          : 'text-green-600'}"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    {feature}
                  </li>
                {/each}
              </ul>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {:else}
    <!-- Loading skeleton -->
    <div
      class="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 px-4"
    >
      {#each [1, 2, 3] as _}
        <div
          class="rounded-2xl p-7 bg-white shadow-md border border-gray-100 animate-pulse"
        >
          <div class="h-6 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
          <div class="h-10 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div class="h-12 bg-gray-200 rounded-full mb-6"></div>
          <div class="space-y-3">
            <div class="h-3 bg-gray-100 rounded w-full"></div>
            <div class="h-3 bg-gray-100 rounded w-5/6"></div>
            <div class="h-3 bg-gray-100 rounded w-4/6"></div>
            <div class="h-3 bg-gray-100 rounded w-full"></div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>
